import { useEditorStore } from "components/Editor/useEditorStore";
import { useBranchEditor } from "features/editor/Feature";
import { useCapabilities } from "hooks/pitcher/useCapabilities";
import { usePermissions } from "hooks/pitcher/usePermissions";

import { useAnalytics } from "hooks/useAnalytics";
import { useGitHubScopes } from "hooks/useGitHubScopes";
import { useOpenPullRequestURL } from "hooks/useOpenPullRequestURL";
import { executeVSCodeCommand } from "utils/vscode";

import type { Command } from "../../types";

export const useOpenPRCommand = (): Command | null => {
  const track = useAnalytics();
  const { git } = useEditorStore();
  const pitcherPermissions = usePermissions();
  const pitcherCapabilities = useCapabilities();

  const gitHubScopes = useGitHubScopes();

  const project = useBranchEditor();

  if (project.type !== "branch") {
    throw new Error("You can not Open PR on a Sandbox");
  }

  const { protectedBranch, contribution, pullRequest } = project;

  const openPRurl = useOpenPullRequestURL();

  const hasLocalChanges = git.status?.localChanges ?? false;

  // PR-related actions should never be available in
  // protected branches or in contribution branches
  // if the user has not integrated GitHub.
  if (protectedBranch || (contribution && !gitHubScopes?.publicRepos)) {
    return null;
  }

  const hasPROpen = pullRequest !== null;
  const viewPR = {
    id: "codesandbox.git.viewPR",
    section: "Git",
    name: "View PR",
    perform: openPRurl,
  };

  // If git is not available to the user, the only possible
  // action is to view an existing PR.
  if (!pitcherCapabilities.git?.pull || !pitcherPermissions.git?.pull) {
    if (hasPROpen) {
      return viewPR;
    }

    return null;
  }

  /**
   * Commiting on the default and unprotect branch
   */
  if (
    project.protectedBranch === false &&
    project.defaultBranchName === project.branch
  ) {
    if (hasLocalChanges) {
      return {
        id: "codesandbox.git.commitChanges",
        section: "Git",
        name: "Commit and push",
        perform: git.commit,
        subtitle: "Commit all changes on the current branch",
      };
    } else {
      // No PR action on the default branch
      return null;
    }
  }

  // Uncommitted changes will show "Create PR" if no PR is linked
  // and "Update PR" if a PR is already open.
  if (hasLocalChanges) {
    return {
      id: "codesandbox.git.createPR",
      section: "Git",
      name: "Create commit",
      perform: () => {
        executeVSCodeCommand("workbench.scm.focus");

        track("git_create_pr");
      },
      subtitle: "Create new commit",
    };
  }

  // We check if you have any commits for the PR by just checking the history, as that is much faster than waiting for the
  // remote state. Though this should only be done when we are not currently on the target branch (Just committing from default branch)
  const hasCommitsForPR =
    git.status.commits.length > 0 &&
    git.status.branch !== git.status.target.branch;

  if (hasCommitsForPR && !hasPROpen) {
    return {
      id: "codesandbox.git.createPR",
      section: "Git",
      name: "Create PR",
      perform: () => {
        openPRurl();

        track("git_create_pr");
      },
      subtitle: "Create pull request",
    };
  }

  // If there are committed changes but no linked PR, the available
  // action is to create a new PR. We only check for commits as it takes too long to
  // check actual ahead/behind state. On protected
  if (hasCommitsForPR && !hasPROpen) {
    return {
      id: "codesandbox.git.createPR",
      section: "Git",
      name: "Open PR",
      perform: openPRurl,
      subtitle: "Create pull request from committed changes",
    };
  }

  if (hasPROpen) {
    return viewPR;
  }

  return null;
};
